/**
 * Utility functions for securely hashing phone numbers for tracking purposes.
 * This ensures that sensitive phone number data is not stored in raw format
 * while maintaining consistency (same phone number always produces same hash).
 */
import md5 from 'md5';

/**
 * Determines if a string looks like a phone number
 * @param {string} value - The value to check
 * @returns {boolean} - True if the value appears to be a phone number
 */
export const isPhoneNumber = value => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  // Remove common phone number formatting characters
  const cleaned = value.replace(/[\s\-()+ ]/g, '');

  // Check if it's all digits and has a reasonable length for a phone number
  // Most phone numbers are between 10-15 digits
  return /^\d{10,15}$/.test(cleaned);
};

/**
 * Normalizes a phone number by removing formatting and ensuring consistent format
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string} - Normalized phone number (digits only)
 */
export const normalizePhoneNumber = phoneNumber => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber;
  }

  // Remove all non-digit characters
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Creates a consistent hash of a phone number using MD5
 * @param {string} phoneNumber - The phone number to hash
 * @returns {string} - Hash of the normalized phone number
 */
export const hashPhoneNumber = phoneNumber => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber;
  }

  // Normalize the phone number first to ensure consistency
  const normalized = normalizePhoneNumber(phoneNumber);

  // If it doesn't look like a phone number, return as-is
  if (!isPhoneNumber(normalized)) {
    return phoneNumber;
  }

  try {
    // Use MD5 for consistent, deterministic hashing
    const hash = md5(normalized);

    // Return first 16 characters with ph_ prefix for readability
    return `ph_${hash.substring(0, 16)}`;
  } catch (error) {
    // Fallback to original value if hashing fails
    console.warn('Failed to hash phone number:', error);
    return phoneNumber;
  }
};
